export const EMPTY_ANSWER_SUBMIT_MESSAGE = 'Cannot post an empty message.'

type DeltaOp = {
    insert?: unknown
}

type DeltaJson = {
    ops?: DeltaOp[]
}

type AnswerLike = {
    content?: unknown
    delta_json?: DeltaJson | string | null
    deltaJson?: DeltaJson | string | null
    embeds?: unknown[]
    url_id?: unknown
    dice?: unknown
    message_type?: unknown
    poll_options?: unknown[]
    action_requests?: unknown[]
    ai_actions?: unknown[]
}

type UploadedAnswerContent = {
    imageSignedId?: string | null
    fileSignedIds?: string[]
    recordingSignedId?: string | null
}

function hasText(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0
}

function deltaHasSubmittableText(deltaJson: DeltaJson | string | null | undefined): boolean {
    if (!deltaJson) return false
    if (typeof deltaJson === 'string') return hasText(deltaJson)

    const ops = Array.isArray(deltaJson.ops) ? deltaJson.ops : []
    return ops.some(op => {
        const insert = op?.insert
        if (typeof insert === 'string') return hasText(insert)
        if (!insert || typeof insert !== 'object') return false

        const embed = insert as Record<string, unknown>
        if ('image' in embed || 'video' in embed) return false
        return Object.keys(embed).length > 0
    })
}

function hasPollContent(answer: AnswerLike): boolean {
    if (answer.message_type !== 'poll') return false
    return Array.isArray(answer.poll_options) && answer.poll_options.some(option => hasText(option))
}

function hasActionRequestContent(answer: AnswerLike): boolean {
    return (
        (Array.isArray(answer.action_requests) &&
            answer.action_requests.some(action => Boolean(action))) ||
        (Array.isArray(answer.ai_actions) && answer.ai_actions.some(action => Boolean(action)))
    )
}

export function hasSubmittableAnswerContent(
    answer: AnswerLike | null | undefined,
    uploaded: UploadedAnswerContent = {},
): boolean {
    if (!answer) return false

    return Boolean(
        hasText(answer.content) ||
            deltaHasSubmittableText(answer.delta_json || answer.deltaJson) ||
            answer.embeds?.length ||
            uploaded.fileSignedIds?.length ||
            uploaded.imageSignedId ||
            uploaded.recordingSignedId ||
            answer.url_id ||
            answer.dice ||
            hasPollContent(answer) ||
            hasActionRequestContent(answer),
    )
}

export function assertSubmittableAnswerContent(
    answer: AnswerLike | null | undefined,
    uploaded: UploadedAnswerContent = {},
): void {
    if (!hasSubmittableAnswerContent(answer, uploaded)) {
        throw new Error(EMPTY_ANSWER_SUBMIT_MESSAGE)
    }
}
